import React, { useState } from 'react';
import { CheckCircle2, Download, Loader2, Mail, Settings2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ptApi } from '@/services/ptFilesApi';

const fmtDay = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtAt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '');
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Status = ({ c, tid, onDesigns }) => {
  if (!c) return <span className="inline-flex items-center gap-1 text-xs text-gray-400" data-testid={`${tid}-checking`}><Loader2 className="w-3.5 h-3.5 animate-spin" />Checking…</span>;
  if (c.ready) {
    return (
      <div data-testid={`${tid}-ready`}>
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700"><CheckCircle2 className="w-4 h-4" />Ready</span>
        <div className="text-xs text-gray-500">{c.lines} line{c.lines === 1 ? '' : 's'} · {c.totals?.qty} pcs · {inr(c.totals?.net)}</div>
        {(c.warnings || []).map((w) => <div key={w} className="text-xs text-amber-700">{w}</div>)}
      </div>
    );
  }
  return (
    <div data-testid={`${tid}-blocked`}>
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-rose-600"><XCircle className="w-4 h-4" />Blocked</span>
      <ul className="mt-0.5 space-y-0.5 text-xs text-rose-700">{(c.problems || []).map((p) => <li key={p}>• {p}</li>)}</ul>
      {(c.missing_designs?.length > 0 || c.missing_hsn?.length > 0) && (
        <button type="button" onClick={() => onDesigns(c)} className="mt-1 text-xs font-medium text-[#2D5F4F] underline" data-testid={`${tid}-add-codes`}>Add the missing codes</button>
      )}
    </div>
  );
};

const Row = ({ r, c, onCodes, onDesigns, onDownloaded, onEmail, canMail }) => {
  const [busy, setBusy] = useState(false);
  const tid = `pt-row-${r.invoice_no.replace(/[^A-Za-z0-9]+/g, '-')}`;
  const go = async () => {
    setBusy(true);
    try {
      const name = await ptApi.download(r.invoice_no);
      toast.success(`${name} downloaded for ${r.invoice_no}`);
      onDownloaded();
    } catch (e) {
      toast.error(e.message, { description: (e.problems || []).join(' · ') });
    } finally { setBusy(false); }
  };
  return (
    <tr className="border-t border-[#EFE8D8] align-top" data-testid={tid}>
      <td className="px-3 py-2.5 whitespace-nowrap"><div className="font-semibold text-[#1A1F2E]">{r.invoice_no}</div><div className="text-xs text-gray-500">{fmtDay(r.invoice_date)}</div></td>
      <td className="px-3 py-2.5 text-xs text-gray-600 min-w-[130px] max-w-[180px]">{r.dcs.map((d) => <div key={d} className="whitespace-nowrap">{d}</div>)}</td>
      <td className="px-3 py-2.5"><div className="text-sm">{r.customer}</div><div className="text-xs text-gray-500">{r.place}{r.file_code ? ` · Shoper ${r.file_code}` : ''}</div></td>
      <td className="px-3 py-2.5 text-sm tabular-nums">{r.invoice_qty ?? '—'}</td>
      <td className="px-3 py-2.5 min-w-[240px]"><Status c={c} tid={tid} onDesigns={onDesigns} /></td>
      <td className="px-3 py-2.5 text-right whitespace-nowrap">
        <Button size="sm" onClick={go} disabled={!c?.ready || busy} className="bg-[#2D5F4F] hover:bg-[#244c3f]" data-testid={`${tid}-download`}>
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}PT file</Button>
        <Button size="sm" variant="outline" onClick={() => onEmail(r)} disabled={!c?.ready || !canMail} title={canMail ? 'Email this PT file to the distributor' : 'Connect Gmail first'} className="ml-1" data-testid={`${tid}-email`}>
          <Mail className="w-4 h-4 mr-1" />Email</Button>
        <Button size="sm" variant="ghost" onClick={() => onCodes({ id: r.customer_id, name: r.customer })} title="Distributor PT codes" data-testid={`${tid}-codes`}><Settings2 className="w-4 h-4" /></Button>
        {r.pt_count > 0 && <div className="ml-auto mt-1 max-w-[220px] whitespace-normal text-[11px] text-gray-500" data-testid={`${tid}-downloaded`}>Downloaded {r.pt_count}× · {fmtAt(r.pt_last_at)}{r.pt_last_by ? ` · ${r.pt_last_by}` : ''}</div>}
        {r.pt_mail_count > 0 && <div className="ml-auto mt-0.5 max-w-[220px] whitespace-normal text-[11px] text-emerald-700" data-testid={`${tid}-emailed`}>Emailed {r.pt_mail_count}× to {(r.pt_mail_last_to || []).join(', ')} · {fmtAt(r.pt_mail_last_at)}</div>}
      </td>
    </tr>
  );
};

export const PtInvoiceTable = ({ rows, checks, onCodes, onDesigns, onDownloaded, onEmail, canMail }) => (
  rows.length === 0 ? <div className="rounded-xl bg-white py-14 text-center text-gray-500 shadow-sm" data-testid="pt-empty">No distributor invoices in these dates — invoices come in through the bulk Sales Invoice PDF upload</div> : (
    <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
      <table className="w-full text-left" data-testid="pt-table">
        <thead className="bg-[#F7F2E7] text-[11px] uppercase tracking-wide text-gray-500">
          <tr><th className="px-3 py-2">Invoice</th><th className="px-3 py-2">DC</th><th className="px-3 py-2">Distributor</th><th className="px-3 py-2">Qty</th><th className="px-3 py-2">Status</th><th className="px-3 py-2" /></tr>
        </thead>
        <tbody>{rows.map((r) => <Row key={r.invoice_no} r={r} c={checks[r.invoice_no]} onCodes={onCodes} onDesigns={onDesigns} onDownloaded={onDownloaded} onEmail={onEmail} canMail={canMail} />)}</tbody>
      </table>
    </div>
  )
);
